import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { giftArt, validateCart } from "@/lib/api";
import { tokenFor } from "@/lib/access";

const CartContext = createContext(null);
const KEY = "sus-cart-v1";

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => localStorage.setItem(KEY, JSON.stringify(items)), [items]);

  const check = async (slug, size, quantity, dropSlug) => {
    try {
      const r = await validateCart({ slug, size, quantity, access_token: tokenFor(dropSlug) });
      if (!r.ok) toast.error(r.message);
      return r.ok;
    } catch (e) {
      toast.error(e.response?.status === 403 ? e.response.data.detail : "Couldn't check stock. Try again.");
      return false;
    }
  };

  const addItem = useCallback(async (product, { color, size, qty, image }) => {
    const key = `${product.slug}|${color}|${size}`;
    const existing = items.find((i) => i.key === key);
    const ok = await check(product.slug, size, (existing?.qty || 0) + qty, product.drop_slug);
    if (!ok) return false;
    setItems((prev) =>
      existing
        ? prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i))
        : [...prev, { key, slug: product.slug, name: product.name, price: product.price, image: image || product.images[0].src, color, size, qty, dropSlug: product.drop_slug || null }],
    );
    toast.success(`${product.name} added. Certified sus.`);
    setOpen(true);
    return true;
  }, [items]);

  const setQty = useCallback(async (key, qty) => {
    const item = items.find((i) => i.key === key);
    if (!item) return;
    if (qty < 1) return setItems((prev) => prev.filter((i) => i.key !== key));
    if (item.kind === "gift_card" && qty > 1) return toast("Add another gift card from the gift card page so each one has its own recipient.");
    if (!item.kind && qty > item.qty && !(await check(item.slug, item.size, qty, item.dropSlug))) return;
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  }, [items]);

  const addBundle = useCallback(async (bundle, components) => {
    for (const c of components) if (!(await check(c.slug, c.size, 1))) return false;
    const key = `bundle|${bundle.slug}|${components.map((c) => `${c.slug}:${c.color}:${c.size}`).join("|")}`;
    const description = components.map((c) => `${c.name} (${c.color}${c.size === "OS" ? "" : ` / ${c.size}`})`).join(" + ");
    setItems((prev) => (prev.some((i) => i.key === key) ? prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i))
      : [...prev, { key, kind: "bundle", slug: bundle.slug, name: bundle.name, price: bundle.price, image: bundle.image, color: "Bundle", size: "SET", qty: 1, components, description }]));
    toast.success(`${bundle.name} added. Certified sus.`);
    setOpen(true);
    return true;
  }, []);

  const addGift = useCallback((g) => {
    const key = `gift|${Date.now()}`;
    setItems((prev) => [...prev, { key, kind: "gift_card", slug: "gift-card", name: `Sus Gift Card C$${g.amount}`, price: g.amount, image: giftArt(g.design, g.amount), color: `${g.design} card`, size: "GIFT", qty: 1, gift: g, description: `For ${g.recipient_name}${g.send_date ? ` · sends ${g.send_date}` : ""}` }]);
    toast.success("Gift card added.");
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((a, i) => a + i.qty, 0);
    const subtotal = items.reduce((a, i) => a + i.qty * i.price, 0);
    return { items, count, subtotal, open, setOpen, addItem, addBundle, addGift, setQty, removeItem, clear };
  }, [items, open, addItem, addBundle, addGift, setQty, removeItem, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const cartPayload = (items) => ({
  items: items.filter((i) => !i.kind).map(({ slug, color, size, qty }) => ({ slug, color, size, qty })),
  bundles: items.filter((i) => i.kind === "bundle").map((i) => ({ bundle_slug: i.slug, qty: i.qty, choices: i.components.map(({ slot, slug, color, size }) => ({ slot, slug, color, size })) })),
  gift_cards: items.filter((i) => i.kind === "gift_card").map((i) => i.gift),
});

export const useCart = () => useContext(CartContext);
